/**
 * Income Sources Panel (SPRINT-6B F004)
 *
 * Toggle-based panel for selecting which income types to include in deal serviceability calculations.
 * Features:
 * - 6 income type toggles (Business, Rental, Personal, Dividend, Trust, Other)
 * - Shading override inputs (0-100%)
 * - Default shading percentages
 * - Calculated totals (Total Gross Income, Total Shaded Income)
 *
 * States:
 * - Empty: No config exists, show "Configure Income Sources" button
 * - Display: Show toggles and overrides
 * - Validating: Show validation errors inline
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest, getAuthHeaders } from '@/lib/queryClient';
import { Plus, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';

// Types
interface IncomeSourcesConfig {
  id: string;
  dealId: string;
  includeBusinessIncome: boolean;
  includeRentalIncome: boolean;
  includePersonalIncome: boolean;
  includeDividendIncome: boolean;
  includeTrustIncome: boolean;
  includeOtherIncome: boolean;
  businessIncomeShading: number | null;
  rentalIncomeShading: number | null;
  personalIncomeShading: number | null;
  dividendIncomeShading: number | null;
  trustIncomeShading: number | null;
  totalGrossIncome: number;
  totalShadedIncome: number;
  createdAt: string;
  updatedAt: string;
}

export interface IncomeSourcesPanelProps {
  dealId: string;
}

// Default shading percentages
const DEFAULT_SHADING = {
  business: 100,
  rental: 80,
  personal: 100,
  dividend: 80,
  trust: 80,
  other: 100,
};

// Income type configuration
const INCOME_TYPES = [
  { key: 'business', label: 'Business (EBITDA)', defaultShading: DEFAULT_SHADING.business },
  { key: 'rental', label: 'Rental', defaultShading: DEFAULT_SHADING.rental },
  { key: 'personal', label: 'Personal (PAYG)', defaultShading: DEFAULT_SHADING.personal },
  { key: 'dividend', label: 'Dividend', defaultShading: DEFAULT_SHADING.dividend },
  { key: 'trust', label: 'Trust Distribution', defaultShading: DEFAULT_SHADING.trust },
  { key: 'other', label: 'Other', defaultShading: DEFAULT_SHADING.other },
] as const;

// Format currency
function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

export const IncomeSourcesPanel: React.FC<IncomeSourcesPanelProps> = ({ dealId }) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [localValues, setLocalValues] = useState<Record<string, string>>({});

  // Fetch config
  const { data: config, isLoading } = useQuery<IncomeSourcesConfig>({
    queryKey: ['dealIncomeSources', dealId],
    queryFn: async () => {
      const authHeaders = await getAuthHeaders();
      const res = await fetch(`/api/crm/deals/${dealId}/income-sources`, {
        headers: authHeaders,
        credentials: "include",
      });
      if (res.status === 404) {
        throw new Error('NOT_FOUND');
      }
      if (!res.ok) throw new Error('Failed to fetch income sources config');
      return res.json();
    },
    retry: false,
  });

  // Create config mutation
  const createMutation = useMutation({
    mutationFn: async (data: Partial<IncomeSourcesConfig>) => {
      const res = await apiRequest('POST', `/api/crm/deals/${dealId}/income-sources`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dealIncomeSources', dealId] });
      toast({
        title: 'Income sources configured',
        description: 'Income sources configuration has been created.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Update config mutation
  const updateMutation = useMutation({
    mutationFn: async (data: Partial<IncomeSourcesConfig>) => {
      const res = await apiRequest('PATCH', `/api/crm/deals/${dealId}/income-sources`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dealIncomeSources', dealId] });
      setValidationErrors({});
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Handle toggle change
  const handleToggleChange = (key: string, checked: boolean) => {
    const field = `include${key.charAt(0).toUpperCase() + key.slice(1)}Income`;
    updateMutation.mutate({ [field]: checked });
  };

  // Handle shading override change
  const handleShadingChange = (key: string, value: string) => {
    // Validate percentage
    const numValue = parseFloat(value);
    const field = `${key}IncomeShading`;

    if (value === '') {
      // Clear override (use default)
      updateMutation.mutate({ [field]: null });
      setValidationErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      return;
    }

    if (isNaN(numValue) || numValue < 0 || numValue > 100) {
      setValidationErrors((prev) => ({
        ...prev,
        [key]: 'Percentage must be between 0 and 100',
      }));
      return;
    }

    setValidationErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });

    updateMutation.mutate({ [field]: numValue });
  };

  // Handle configure button click
  const handleConfigure = () => {
    createMutation.mutate({
      includeBusinessIncome: true,
      includeRentalIncome: false,
      includePersonalIncome: false,
      includeDividendIncome: false,
      includeTrustIncome: false,
      includeOtherIncome: false,
    });
  };

  if (isLoading) {
    return (
      <Card data-testid="panel-income-sources">
        <CardHeader>
          <CardTitle>Income Sources</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </CardContent>
      </Card>
    );
  }

  // Empty state
  if (!config) {
    return (
      <Card data-testid="panel-income-sources">
        <CardHeader>
          <CardTitle>Income Sources</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <p className="text-muted-foreground mb-4">No income sources configured</p>
            <Button onClick={handleConfigure} disabled={createMutation.isPending}>
              {createMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="mr-2 h-4 w-4" />
                  Configure Income Sources
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Display mode
  return (
    <Card data-testid="panel-income-sources">
      <CardHeader>
        <CardTitle>Income Sources</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Income type toggles */}
        <div className="space-y-4">
          {INCOME_TYPES.map((incomeType) => {
            const key = incomeType.key;
            const toggleField = `include${key.charAt(0).toUpperCase() + key.slice(1)}Income` as keyof IncomeSourcesConfig;
            const shadingField = `${key}IncomeShading` as keyof IncomeSourcesConfig;
            const isIncluded = config[toggleField] as boolean;
            const shadingOverride = config[shadingField] as number | null;
            // Use local value if being edited, otherwise use config value
            const displayValue = key in localValues ? localValues[key] : (shadingOverride ?? '');

            return (
              <div key={key} className="grid grid-cols-12 gap-4 items-start border-b pb-4">
                {/* Label */}
                <div className="col-span-4">
                  <Label className="text-sm font-medium">{incomeType.label}</Label>
                </div>

                {/* Toggle */}
                <div className="col-span-2 flex items-center">
                  <Switch
                    checked={isIncluded}
                    onCheckedChange={(checked) => handleToggleChange(key, checked)}
                    data-testid={`toggle-${key}-income`}
                    disabled={updateMutation.isPending}
                  />
                </div>

                {/* Default percentage display */}
                <div className="col-span-2 flex items-center text-sm text-muted-foreground">
                  Default: {incomeType.defaultShading}%
                </div>

                {/* Override input */}
                <div className="col-span-4">
                  <div className="space-y-1">
                    <Input
                      type="number"
                      step="0.01"
                      value={displayValue}
                      onChange={(e) => {
                        // Update local state only - don't validate or save yet
                        setLocalValues((prev) => ({ ...prev, [key]: e.target.value }));
                        // Clear any existing validation error while typing
                        setValidationErrors((prev) => {
                          const next = { ...prev };
                          delete next[key];
                          return next;
                        });
                      }}
                      onBlur={(e) => {
                        // Clear local state
                        setLocalValues((prev) => {
                          const next = { ...prev };
                          delete next[key];
                          return next;
                        });
                        // Validate and save
                        handleShadingChange(key, e.target.value);
                      }}
                      placeholder={`Override (default: ${incomeType.defaultShading}%)`}
                      data-testid={`shading-input-${key}`}
                      disabled={updateMutation.isPending}
                      className={`text-sm ${validationErrors[key] ? 'border-destructive' : ''}`}
                    />
                    {validationErrors[key] && (
                      <p className="text-xs text-destructive" data-testid={`error-${key}`}>
                        {validationErrors[key]}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Totals section */}
        <div className="border-t pt-4 space-y-3">
          <div className="flex justify-between items-center">
            <Label className="text-sm font-medium">Total Gross Income</Label>
            <span
              className="text-lg font-semibold"
              data-testid="total-gross-income"
            >
              {formatCurrency(config.totalGrossIncome)}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <Label className="text-sm font-medium">Total Shaded Income</Label>
            <span
              className="text-lg font-semibold text-primary"
              data-testid="total-shaded-income"
            >
              {formatCurrency(config.totalShadedIncome)}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
